import { PaymentType } from "@1games-ru/region-shared/dist/enums/payment/payment";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import BusinessMpProvider from "@/core/data-provider/data-mp/business/business-mp-provider";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import { RpcMpProviderSuccessResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-success-response";
import { DeviceEnum } from "@/domains/business/constants/deviceEnum";
import {
  appDeviceSelector,
  infoSelector,
  pcInfoSelector,
  setInfo,
  setPcInfo,
} from "@/domains/business/slice";
import toast from "@/domains/toasts/helpers/toast";

export default function useTopUpBusinessBalance(businessId: number) {
  const { t, i18n } = useTranslation("business");
  const dispatch = useAppDispatch();

  const [isTopUpBalanceLoading, setIsTopUpBalanceLoading] = useState(false);

  const divice = useAppSelector(appDeviceSelector);
  const pcInfo = useAppSelector(pcInfoSelector);
  const tabletInfo = useAppSelector(infoSelector);

  const topUpBalance = useCallback(
    async (amount: number, paymentType: PaymentType, cb: () => void) => {
      if (businessId && amount && paymentType) {
        setIsTopUpBalanceLoading(true);

        try {
          const response = await BusinessMpProvider.topUpBusinessBalance(
            businessId,
            amount,
            paymentType,
          );

          if (response instanceof RpcMpProviderSuccessResponse) {
            toast.success(t("business.toast.topUpBalanceUpdated"));
            cb();
            if (divice === DeviceEnum.PC && pcInfo) {
              const prevPrice = pcInfo.expenseBalance;
              dispatch(setPcInfo({ ...pcInfo, expenseBalance: prevPrice + amount }));
              if (pcInfo.id === tabletInfo?.id) {
                dispatch(setInfo({ ...tabletInfo, expenseBalance: prevPrice + amount }));
              }
            }
            if (divice === DeviceEnum.TABLET && tabletInfo) {
              const prevPrice = tabletInfo.expenseBalance;
              dispatch(setInfo({ ...tabletInfo, expenseBalance: prevPrice + amount }));
            }
          } else if (i18n.exists(`business:business.errors.${response.error.message}`)) {
            console.error(response.error.message);
            toast.error(t(`business.errors.${response.error.message}`));
          } else {
            console.error(response.error.message);
            toast.error(t("business.toast.topUpBalanceError"));
          }
        } catch (e) {
          console.error(e);
          toast.error(t("business.toast.topUpBalanceError"));
        } finally {
          setIsTopUpBalanceLoading(false);
        }
      }
    },
    [businessId, dispatch, divice, i18n, pcInfo, t, tabletInfo],
  );

  return {
    isTopUpBalanceLoading,
    topUpBalance,
  };
}
